import { useState, useEffect } from 'react';
import {
  addNotification,
  consumeFromPlan,
  getPlanConsumption,
  pushNotificationTypes,
  channelLabels,
  channelIcons,
  channelMockBadge,
  audienceLabels,
  audienceCounts,
  type NotificationStatus,
  type NotificationChannel,
  type NotificationAudience,
  type NotificationModule,
  type PushNotificationType,
} from '@/mocks/adminNotificationEngine';
import { SwalAlert } from '@/hooks/useSweetAlert';

export interface NotificationFlowContext {
  type: PushNotificationType;
  relatedModule: NotificationModule;
  relatedItemId?: number;
  relatedItemName?: string;
  prefilledTitle?: string;
  prefilledMessage?: string;
}

interface NotificationFlowModalProps {
  open: boolean;
  context: NotificationFlowContext | null;
  onClose: () => void;
}

export default function NotificationFlowModal({ open, context, onClose }: NotificationFlowModalProps) {
  const [audience, setAudience] = useState<NotificationAudience>('todos');
  const [channel, setChannel] = useState<NotificationChannel>('push');
  const [language, setLanguage] = useState('es');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<NotificationStatus>('sent');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when context changes
  useEffect(() => {
    if (context) {
      setTitle(context.prefilledTitle || '');
      setMessage(context.prefilledMessage || '');
      setAudience('todos');
      setChannel('push');
      setLanguage('es');
      setStatus('sent');
      setErrors({});
    }
  }, [context?.relatedItemId, context?.type, open]);

  if (!open || !context) return null;

  const typeMeta = pushNotificationTypes.find((t) => t.type === context.type)!;
  const consumption = getPlanConsumption();
  const isChannelMock = channel !== 'push';

  const handleSubmit = () => {
    const newErrors: Record<string, string> = {};
    if (!title.trim()) newErrors.title = 'El título es obligatorio';
    if (title.length > 80) newErrors.title = 'Máximo 80 caracteres';
    if (!message.trim()) newErrors.message = 'El mensaje es obligatorio';
    if (message.length > 200) newErrors.message = 'Máximo 200 caracteres';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (status !== 'draft') {
      const consumed = consumeFromPlan();
      if (!consumed) {
        SwalAlert.warning('Límite alcanzado', 'Ya usaste todas las notificaciones de tu plan este mes.');
        return;
      }
    }

    const recipients = status === 'sent' ? audienceCounts[audience] : 0;
    const now = new Date().toISOString();

    const notification = addNotification({
      type: context.type,
      title: title.trim(),
      message: message.trim(),
      status,
      channel,
      audience,
      language,
      relatedModule: context.relatedModule,
      relatedItemId: context.relatedItemId,
      relatedItemName: context.relatedItemName,
      sentAt: status === 'sent' ? now : null,
      scheduledFor: null,
      recipients,
      delivered: 0,
      opened: 0,
    });

    const statusLabel = status === 'sent' ? 'enviada' : status === 'draft' ? 'guardada como borrador' : 'programada';
    const channelNote = isChannelMock ? ` (${channelLabels[channel]} simulado)` : '';

    SwalAlert.toast(
      `Notificación "${title.trim()}" ${statusLabel}${channelNote}.`,
      'success',
    );

    onClose();
  };

  const audienceOptions: NotificationAudience[] = ['todos', 'oyentes_activos', 'seguidores', 'vip'];
  const channelOptions: NotificationChannel[] = ['push', 'email', 'whatsapp', 'in_app'];
  const languageOptions = [
    { value: 'es', label: 'Español' },
    { value: 'en', label: 'English' },
    { value: 'pt', label: 'Português' },
  ];
  const statusOptions: { value: NotificationStatus; label: string; icon: string }[] = [
    { value: 'sent', label: 'Enviar ahora', icon: 'ri-send-plane-line' },
    { value: 'draft', label: 'Guardar borrador', icon: 'ri-draft-line' },
    { value: 'scheduled', label: 'Programar', icon: 'ri-time-line' },
  ];

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/40 flex items-start justify-center p-4 pt-[5vh] overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-background-50 rounded-2xl border border-background-200 w-full max-w-lg overflow-hidden shadow-xl animate-fade-in-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl ${typeMeta.bgColor} flex items-center justify-center`}>
              <i className={`${typeMeta.icon} ${typeMeta.color} text-base`}></i>
            </div>
            <div>
              <h3 className="text-foreground-950 font-bold text-sm">Crear notificación</h3>
              <p className="text-foreground-400 text-[10px]">{typeMeta.label}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors shrink-0"
          >
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5 max-h-[65vh] overflow-y-auto">
          {/* Related item context */}
          {context.relatedItemName && (
            <div className="bg-background-100 rounded-xl p-3 border border-background-200 flex items-center gap-2.5">
              <i className="ri-link text-foreground-400 text-sm shrink-0"></i>
              <div className="min-w-0">
                <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wide">Relacionado con</p>
                <p className="text-foreground-700 text-xs font-semibold truncate">{context.relatedItemName}</p>
              </div>
            </div>
          )}

          {/* Step 1: Audience */}
          <div>
            <label className="block text-foreground-700 text-[11px] font-bold mb-2">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-foreground-200 text-foreground-600 text-[10px] font-bold mr-1.5">1</span>
              Audiencia
            </label>
            <div className="grid grid-cols-2 gap-2">
              {audienceOptions.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setAudience(a)}
                  className={`flex flex-col items-start gap-0.5 px-3 py-2.5 rounded-xl border cursor-pointer transition-all text-left ${
                    audience === a
                      ? 'border-accent-500 bg-accent-50'
                      : 'border-background-200 bg-background-50 hover:bg-background-100'
                  }`}
                >
                  <span className={`text-xs font-semibold ${audience === a ? 'text-accent-700' : 'text-foreground-600'}`}>
                    {audienceLabels[a]}
                  </span>
                  <span className="text-[10px] text-foreground-400">{audienceCounts[a].toLocaleString()} oyentes</span>
                </button>
              ))}
            </div>
          </div>

          {/* Step 2: Channel */}
          <div>
            <label className="block text-foreground-700 text-[11px] font-bold mb-2">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-foreground-200 text-foreground-600 text-[10px] font-bold mr-1.5">2</span>
              Canal
            </label>
            <div className="grid grid-cols-2 gap-2">
              {channelOptions.map((ch) => (
                <button
                  key={ch}
                  type="button"
                  onClick={() => setChannel(ch)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border cursor-pointer transition-all ${
                    channel === ch
                      ? 'border-accent-500 bg-accent-50'
                      : 'border-background-200 bg-background-50 hover:bg-background-100'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg ${channel === ch ? 'bg-accent-100' : 'bg-background-100'} flex items-center justify-center shrink-0`}>
                    <i className={`${channelIcons[ch]} ${channel === ch ? 'text-accent-600' : 'text-foreground-400'} text-sm`}></i>
                  </div>
                  <div className="text-left min-w-0">
                    <span className={`text-xs font-semibold block ${channel === ch ? 'text-accent-700' : 'text-foreground-600'}`}>
                      {channelLabels[ch]}
                    </span>
                    {channelMockBadge[ch] && (
                      <span className="text-[9px] text-amber-500 font-medium">{channelMockBadge[ch]}</span>
                    )}
                    {!channelMockBadge[ch] && (
                      <span className="text-[9px] text-foreground-400">{consumption.remaining} disponibles</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Step 3: Language */}
          <div>
            <label className="block text-foreground-700 text-[11px] font-bold mb-2">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-foreground-200 text-foreground-600 text-[10px] font-bold mr-1.5">3</span>
              Idioma
            </label>
            <div className="flex items-center gap-2">
              {languageOptions.map((lang) => (
                <button
                  key={lang.value}
                  type="button"
                  onClick={() => setLanguage(lang.value)}
                  className={`px-4 py-2 rounded-xl border text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                    language === lang.value
                      ? 'border-accent-500 bg-accent-50 text-accent-700'
                      : 'border-background-200 bg-background-50 text-foreground-500 hover:bg-background-100'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>

          {/* Step 4: Message */}
          <div>
            <label className="block text-foreground-700 text-[11px] font-bold mb-2">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-foreground-200 text-foreground-600 text-[10px] font-bold mr-1.5">4</span>
              Mensaje
            </label>

            <div className="space-y-3">
              <div>
                <label className="block text-foreground-500 text-[10px] font-semibold mb-1">Título</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => { setTitle(e.target.value); setErrors((prev) => ({ ...prev, title: '' })); }}
                  placeholder="Ej: ¡Evento confirmado!"
                  maxLength={80}
                  className={`w-full px-3.5 py-2.5 rounded-xl bg-background-100 border text-foreground-800 text-sm placeholder:text-foreground-400 outline-none focus:border-accent-500 transition-colors ${
                    errors.title ? 'border-red-400' : 'border-background-200'
                  }`}
                />
                <div className="flex items-center justify-between mt-1">
                  {errors.title && <p className="text-red-500 text-[9px]">{errors.title}</p>}
                  <span className="text-foreground-400 text-[9px] ml-auto">{title.length}/80</span>
                </div>
              </div>

              <div>
                <label className="block text-foreground-500 text-[10px] font-semibold mb-1">Contenido</label>
                <textarea
                  value={message}
                  onChange={(e) => { setMessage(e.target.value); setErrors((prev) => ({ ...prev, message: '' })); }}
                  placeholder="Escribí el mensaje que recibirán tus oyentes..."
                  maxLength={200}
                  rows={3}
                  className={`w-full px-3.5 py-2.5 rounded-xl bg-background-100 border text-foreground-800 text-sm placeholder:text-foreground-400 outline-none focus:border-accent-500 transition-colors resize-none ${
                    errors.message ? 'border-red-400' : 'border-background-200'
                  }`}
                ></textarea>
                <div className="flex items-center justify-between mt-1">
                  {errors.message && <p className="text-red-500 text-[9px]">{errors.message}</p>}
                  <span className="text-foreground-400 text-[9px] ml-auto">{message.length}/200</span>
                </div>
              </div>
            </div>

            {/* Preview */}
            {title.trim() && message.trim() && (
              <div className="mt-3 bg-background-100 rounded-xl p-3 border border-background-200">
                <p className="text-foreground-400 text-[9px] font-semibold mb-1.5 uppercase tracking-wide">Vista previa</p>
                <div className="flex items-start gap-2.5">
                  <div className={`w-8 h-8 rounded-lg ${typeMeta.bgColor} flex items-center justify-center shrink-0 mt-0.5`}>
                    <i className={`${typeMeta.icon} ${typeMeta.color} text-sm`}></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-foreground-950 font-bold text-xs">{title.trim()}</p>
                    <p className="text-foreground-500 text-[11px] leading-relaxed">{message.trim()}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Step 5: Status */}
          <div>
            <label className="block text-foreground-700 text-[11px] font-bold mb-2">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-foreground-200 text-foreground-600 text-[10px] font-bold mr-1.5">5</span>
              Acción
            </label>
            <div className="grid grid-cols-3 gap-2">
              {statusOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setStatus(opt.value)}
                  className={`flex flex-col items-center gap-1 px-3 py-2.5 rounded-xl border cursor-pointer transition-all ${
                    status === opt.value
                      ? 'border-accent-500 bg-accent-50'
                      : 'border-background-200 bg-background-50 hover:bg-background-100'
                  }`}
                >
                  <i className={`${opt.icon} ${status === opt.value ? 'text-accent-600' : 'text-foreground-400'} text-base`}></i>
                  <span className={`text-[10px] font-semibold whitespace-nowrap ${status === opt.value ? 'text-accent-700' : 'text-foreground-500'}`}>
                    {opt.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Plan consumption info */}
          {status !== 'draft' && (
            <div className="flex items-center gap-2 text-foreground-400 text-[10px] bg-background-100 rounded-xl p-2.5">
              <i className="ri-information-line text-xs shrink-0"></i>
              <span>
                {isChannelMock
                  ? `Se simulará el envío por ${channelLabels[channel]}. Sin consumo del plan.`
                  : `Se consumirá 1 notificación. Te quedarán ${consumption.remaining - 1} de ${consumption.planTotal} este mes.`
                }
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-background-200">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-xs font-semibold text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            className={`px-5 py-2 rounded-xl text-xs font-bold transition-colors cursor-pointer whitespace-nowrap ${
              status === 'sent'
                ? 'bg-accent-500 text-background-50 hover:bg-accent-600'
                : status === 'scheduled'
                  ? 'bg-amber-500 text-background-50 hover:bg-amber-600'
                  : 'bg-foreground-500 text-background-50 hover:bg-foreground-600'
            }`}
          >
            <i className={`${status === 'sent' ? 'ri-send-plane-line' : status === 'scheduled' ? 'ri-time-line' : 'ri-draft-line'} text-xs mr-1.5`}></i>
            {status === 'sent' ? 'Enviar ahora' : status === 'scheduled' ? 'Programar' : 'Guardar borrador'}
          </button>
        </div>
      </div>
    </div>
  );
}